import DeviceUtility from "@/helpers/device";
import { logInfo } from "@/helpers/logError";
import { uploadFailureLogDetails } from "@/services/uploads/uploadLogRedaction";
import {
	estimatePersistentStorageQuota,
	persistStorage,
	requestPersistentStorageWithoutPrompt,
	type PersistentStorageQuotaEstimate
} from "@common-ui/utilities/persistentStorage";

const CURRENT_STEP = "dashboard-file-upload";

export type UploadStoragePersistenceOutcome = "persisted" | "denied" | "unavailable" | "error";

/**
 * Firefox answers a prompt-state persist() with a doorhanger, and its promise can stay pending until
 * the user acts on it. The upload awaits this outcome before any target or PUT, so bound the wait and
 * treat a timeout as not persisted.
 */
export const PERSIST_PROMPT_TIMEOUT_MS = 2_500;

async function persistStorageWithTimeout(onRejectionAfterTimeout: (error: unknown) => void): Promise<boolean | undefined> {
	let timeoutId: ReturnType<typeof setTimeout> | undefined;
	let timedOut = false;
	const persistAttempt = persistStorage();
	// After the timeout the race no longer observes the attempt; a later rejection is a storage failure, not a denial.
	persistAttempt.catch((error: unknown) => {
		if (timedOut) onRejectionAfterTimeout(error);
	});
	try {
		return await Promise.race([
			persistAttempt,
			new Promise<false>((resolve) => {
				timeoutId = setTimeout(() => {
					timedOut = true;
					resolve(false);
				}, PERSIST_PROMPT_TIMEOUT_MS);
			})
		]);
	} finally {
		clearTimeout(timeoutId);
	}
}

function buildTelemetryPayload(conversationId?: string, organizationId?: string | null) {
	return {
		conversationId,
		organizationId,
		currentStep: CURRENT_STEP,
		browser: DeviceUtility.browser,
		browserVersion: DeviceUtility.browserVersion,
		isIOS: DeviceUtility.isIOS,
		userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "unknown"
	};
}

export async function requestUploadStoragePersistence(
	conversationId?: string,
	organizationId?: string | null
): Promise<UploadStoragePersistenceOutcome> {
	try {
		const persistence = await requestPersistentStorageWithoutPrompt();
		if (persistence === "persisted") {
			return "persisted";
		}

		if (persistence === "never") {
			console.warn("[Upload] Persistent storage is not available; continuing with best-effort browser storage.", {
				conversationId,
				organizationId
			});
			logInfo("adminPersistentStorageDenied", {
				...buildTelemetryPayload(conversationId, organizationId),
				quota: undefined,
				usage: undefined,
				outcome: "unavailable"
			});
			return "unavailable";
		}

		// "denied" = permission was granted but the browser still refused to persist; the helper has
		// already attempted persist() internally, so classify it directly as a denial. For "prompt" we
		// make the attempt here and treat a false result as the same denial.
		const reportRejectionAfterTimeout = (error: unknown) => {
			console.warn("[Upload] Persistent storage request failed after the prompt timeout", uploadFailureLogDetails(error));
			logInfo("adminPersistentStorageError", {
				...buildTelemetryPayload(conversationId, organizationId),
				...uploadFailureLogDetails(error),
				afterPromptTimeout: true
			});
		};
		if (persistence === "prompt" && (await persistStorageWithTimeout(reportRejectionAfterTimeout)) !== false) {
			return "persisted";
		}

		let estimate: PersistentStorageQuotaEstimate | undefined;
		try {
			estimate = await estimatePersistentStorageQuota();
		} catch (estimateError) {
			console.warn("[Upload] Failed to estimate storage after persistent storage was denied", uploadFailureLogDetails(estimateError));
		}

		console.warn("[Upload] Persistent storage request was denied; continuing with best-effort browser storage.", {
			conversationId,
			organizationId,
			quota: estimate?.quota,
			usage: estimate?.usage
		});

		logInfo("adminPersistentStorageDenied", {
			...buildTelemetryPayload(conversationId, organizationId),
			quota: estimate?.quota,
			usage: estimate?.usage,
			outcome: "denied"
		});
		return "denied";
	} catch (error) {
		console.error("[Upload] Failed to initialize storage persistence", uploadFailureLogDetails(error));
		logInfo("adminPersistentStorageError", {
			...buildTelemetryPayload(conversationId, organizationId),
			...uploadFailureLogDetails(error)
		});
		return "error";
	}
}
